import FetchAiModels from '@Application/Ai/AiConfiguration/Models/Queries/FetchAiModels';
import fetchAiModels from '@Application/Ai/AiConfiguration/Models/fetchAiModels';
import FetchAiProviders from '@Application/Ai/AiConfiguration/Providers/Queries/FetchAiProviders';
import fetchAiProviders from '@Application/Ai/AiConfiguration/Providers/fetchAiProviders';
import { PermissionEnum } from '@Application/Security/Roles';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import AiEnginesSkeleton from '@UI/Pages/Administration/AiEngines/AiEnginesSkeleton';
import ModelsSection from '@UI/Pages/Administration/AiEngines/Sections/ModelsSection';
import ProvidersSection from '@UI/Pages/Administration/AiEngines/Sections/ProvidersSection';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { Bot, Cpu, Server } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { TabPanel } from '@verclam/ui-kit';
import type { TabViewTabChangeEvent } from '@verclam/ui-kit';
import PillTabView from '@Guidelines/Component/PillTabView';
import useAiEnginesTab from '@UI/Pages/Administration/AiEngines/Hooks/useAiEnginesTab';
import type { AiEnginesTab } from '@UI/Pages/Administration/AiEngines/Hooks/useAiEnginesTab';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';

const TAB_ORDER: readonly AiEnginesTab[] = ['providers', 'models'];

type AiEnginesTabEntry = {
	readonly tab: AiEnginesTab;
	readonly header: string;
	readonly icon: React.ReactNode;
	readonly content: React.ReactNode;
};

const sectionsContainerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 2rem;
	overflow-y: auto;
	padding: 0 1rem;
`;

/**
 * Owns the two queries the screen needs (providers, catalogue), the six full-screen states, and the
 * composition of the two sections as tabs ("Fournisseurs", "Modèles"), the active one kept in the
 * `tab` query parameter. Each tab exists only under its read permission; a single remaining tab
 * renders bare, without the tab bar. Affecting a model to a usage lives on the three dedicated
 * affectation screens (`UI/Pages/AiAssignments/`), each gated on its own permission.
 */
const AiEnginesContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const canReadProviders = useHasPermission(PermissionEnum.AI_PROVIDER_READ);
	const canReadModels = useHasPermission(PermissionEnum.AI_MODEL_READ);

	const availableTabs = React.useMemo(
		() => TAB_ORDER.filter((tab) => (tab === 'providers' ? canReadProviders : canReadModels)),
		[canReadProviders, canReadModels],
	);

	const { activeIndex, setActiveIndex } = useAiEnginesTab(availableTabs);

	const handleTabChange = React.useCallback(
		(event: TabViewTabChangeEvent) => {
			setActiveIndex(event.index);
		},
		[setActiveIndex],
	);

	const {
		data: providers,
		error: providersError,
		refetch: refetchProviders,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.providers,
		stopOn: (value) => !!value && value.scopeId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAiProviders(new FetchAiProviders(accountId)));
		},
		deps: [accountId],
	});

	const {
		data: models,
		error: modelsError,
		refetch: refetchModels,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.models,
		stopOn: (value) => !!value && value.scopeId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAiModels(new FetchAiModels(accountId)));
		},
		deps: [accountId],
	});

	if (accountId === null) {
		return (
			<EmptyState
				title="Aucun compte"
				description="Vous n’êtes rattaché à aucun compte : les moteurs d’IA ne peuvent pas être configurés."
				icon={<Bot size={28} />}
			/>
		);
	}

	if (providers === null && providersError === null) {
		return <AiEnginesSkeleton />;
	}

	if (providers === null) {
		return (
			<ErrorState
				title={'Impossible de charger la configuration des moteurs d’IA.'}
				onRetry={refetchProviders}
			/>
		);
	}

	if (models === null && modelsError === null) {
		return <AiEnginesSkeleton />;
	}

	if (models === null) {
		return (
			<ErrorState
				title={'Impossible de charger la configuration des moteurs d’IA.'}
				onRetry={refetchModels}
			/>
		);
	}

	const allEntries: AiEnginesTabEntry[] = [
		{
			tab: 'providers',
			header: `Fournisseurs (${providers.list.length})`,
			icon: <Server size={18} />,
			content: <ProvidersSection providers={providers} />,
		},
		{
			tab: 'models',
			header: `Modèles (${models.list.length})`,
			icon: <Cpu size={18} />,
			content: <ModelsSection models={models} />,
		},
	];
	const entries = allEntries.filter((entry) => availableTabs.includes(entry.tab));

	if (entries.length > 1) {
		return (
			<div css={sectionsContainerStyle}>
				<PillTabView activeIndex={activeIndex} onTabChange={handleTabChange}>
					{entries.map((entry) => (
						<TabPanel key={entry.tab} header={entry.header} leftIcon={entry.icon}>
							{entry.content}
						</TabPanel>
					))}
				</PillTabView>
			</div>
		);
	}

	return <div css={sectionsContainerStyle}>{entries[0]?.content}</div>;
};

export default AiEnginesContent;
